import { CommonModule } from '@angular/common';
import { Component, effect, Inject, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import { OutlinedButtonComponent, TextButtonComponent } from '@custom-components/general/button';
import { PartnerSelectComponent } from '@custom-components/general/form/partner-select/partner-select.component';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { BrowseFileDirective } from '@core/directives/browse-file.directive';
import { FilesDropDirective } from '@core/directives/files-drop.directive';
import { DeviceType } from '@core/models';
import {
  ConfigFileModel,
  ConfigFileUploading,
  InitialConfigFileModel,
} from '@core/models/setting.model';
import { UploadingStatus } from '@core/models/shipment.model';
import { SettingsService } from '@core/services/setting.service';
import { TranslateModule } from '@ngx-translate/core';
import { DeviceStore } from '@root/app/store/device.store';
import { SettingsStore } from '@root/app/store/settings.store';
import { take } from 'rxjs';

@Component({
  selector: 'xemex-config-file-upload-dialog',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatCheckboxModule,
    TranslateModule,
    SpinnerComponent,
    OutlinedButtonComponent,
    TextButtonComponent,
    PartnerSelectComponent,
    FilesDropDirective,
    BrowseFileDirective,
    MatProgressBarModule,
  ],
  standalone: true,
  templateUrl: './config-file-upload-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './config-file-upload-dialog.component.scss',
})
export class ConfigFileUploadDialogComponent {
  settingsStore = inject(SettingsStore);
  deviceStore = inject(DeviceStore);
  settingsService = inject(SettingsService);

  readonly dialogRef = inject(MatDialogRef<ConfigFileUploadDialogComponent>);

  acceptedTypes = ['.json', '.xml'];
  isUploading: boolean = false;
  uploadingStatus: UploadingStatus = UploadingStatus.Successful;
  UploadingStatus = UploadingStatus;
  fileUpload: File | null = null;
  deviceTypes: DeviceType[] | undefined;
  selectedDeviceTypeId: number | null = null;
  existingFile: ConfigFileModel | InitialConfigFileModel | null = null;
  allowOverride: boolean = false;
  isCheckingFile: boolean = false;

  // Partner picker: OIDs of the partners the file is shared with.
  readonly partnersControl = new FormControl<string[]>([], { nonNullable: true });

  constructor(@Inject(MAT_DIALOG_DATA) public data: { isDeviceConfigFile: boolean }) {
    effect(() => {
      this.uploadingStatus = this.settingsStore.configurationFileUploadingStatus();
      this.isUploading = this.settingsStore.isUploading();
    });
    effect(() => {
      this.deviceTypes = this.deviceStore
        .deviceTypes()
        ?.[
          'device-types'
        ].filter(d => d.supportsTopology && (data.isDeviceConfigFile || d.protocol == 'DLMS'));
      if (!this.deviceTypes) {
        this.deviceStore.getDeviceTypesMethod();
      }
    });
  }

  onUploadFile(files: File[]) {
    this.fileUpload = files[0];
    this.existingFile = null;
    this.allowOverride = false;
    this.isCheckingFile = true;

    if (this.data.isDeviceConfigFile) {
      this.settingsService
        .getDeviceConfigFiles({
          filename: files[0].name,
          includeDeleted: true,
          limit: 1,
          offset: 0,
        })
        .pipe(take(1))
        .subscribe({
          next: data => {
            this.existingFile = data.files?.[0] ?? null;
            this.isCheckingFile = false;
          },
          error: () => {
            this.isCheckingFile = false;
          },
        });
    } else {
      this.settingsService
        .getInitialConfigFiles({
          filename: files[0].name,
          includeDeleted: true,
          limit: 1,
          offset: 0,
        })
        .pipe(take(1))
        .subscribe({
          next: data => {
            this.existingFile = data.files?.[0] ?? null;
            this.isCheckingFile = false;
          },
          error: () => {
            this.isCheckingFile = false;
          },
        });
    }
  }

  onUploadCF() {
    if (!this.fileUpload) {
      return;
    }
    const payload: ConfigFileUploading = {
      file: this.fileUpload,
      deviceType: '' + this.selectedDeviceTypeId,
      partnerOids: this.partnersControl.value,
      firmwareVersions: [],
    };
    if (this.data.isDeviceConfigFile) {
      this.settingsStore.uploadDCFCMethod(payload);
    } else {
      this.settingsStore.uploadICFMethod(payload);
    }
  }

  resetStatus() {
    this.settingsStore.resetUploadingStatusMethod();
  }
}
